import React                       from 'react';
import View                        from '@widesk-ui/components/View';
import useForceUpdate              from '@widesk-core/hooks/useForceUpdate';
import { observer }                from 'mobx-react-lite';
import { DrawTriggerOptions }      from '@tokamap/core';
import { Shape }                   from '@tokamap/core';
import { formatDistance }          from '@tokamap/core';
import { lineWallThicknessMeters } from '@tokamap/core';
import Field                       from '@/TokamapEditor/ux/AreaSidebar/Field';
import SliderInputNumber           from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import currentEditorStore          from '@/TokamapEditor/stores/currentEditorStore';
import themeStore                  from '@/TokamapEditor/stores/themeStore';
import useDesignTokens             from '@/hooks/useDesignTokens';
import HelpTip                     from '@/TokamapEditor/ux/AreaSidebar/HelpTip';

const LIVE: DrawTriggerOptions = { skipHistory: true, skipSave: true, skipConfirm: true };

export const SLIDER_MAX_HEIGHT_METERS = 300;
export const MAX_HEIGHT_METERS = 1000;

const themeOf = (shape: Shape) => (shape.themeId ? themeStore.getTheme(shape.themeId) : undefined);

/**
 * Hauteur et hauteur au-dessus du sol, au repos ou au survol (`hover`). Comme pour la bordure, le
 * survol n'affiche que ce qui y est réglé : à zéro, il reprend la valeur du repos.
 */
const ThreeDOptions = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const forceUpdate = useForceUpdate();

	React.useEffect(() => {
		return editor.mapAdapter.on(event => {
			if (event.type === 'allow-pitch:changed') forceUpdate();
		});
	}, [editor]);

	if (!shapes.length || !editor.getAllowPitch()) return null;
	if (shapes.some(shape => shape.shapeKind === 'image')) return null;

	const restHeight = (shape: Shape) => themeOf(shape)?.height ?? shape.height;
	const restElevation = (shape: Shape) => themeOf(shape)?.elevation ?? shape.elevation;
	const height = (shape: Shape) => (hover ? shape.hoverHeight : restHeight(shape));
	const elevation = (shape: Shape) => (hover ? shape.hoverElevation : restElevation(shape));
	// Le volume tel qu'il s'affiche au survol, qui décide si décoller du sol a un sens.
	const effectiveHeight = (shape: Shape) => (hover ? shape.hoverHeight ?? restHeight(shape) : restHeight(shape));

	// Au repos, une valeur imposée par le thème ne se règle pas ici ; au survol, le thème n'en a pas.
	const heightShapes = hover ? shapes : shapes.filter(shape => !themeOf(shape)?.height);
	const elevationShapes = shapes.filter(shape => (hover || !themeOf(shape)?.elevation) && !!effectiveHeight(shape));

	if (!heightShapes.length && !elevationShapes.length) return null;

	const lines = shapes.every(shape => shape.shapeKind === 'line');
	const toModel = (meters: number) => (meters > 0 ? meters : undefined);

	return (
		<>
			<MetersField
				label="Hauteur"
				help={hover
					? 'Hauteur au survol, visible quand la carte est inclinée. À zéro, celle de l\'état normal.'
					: lines
						? 'Élève la ligne en mur quand la carte est inclinée. À zéro, elle reste un trait.'
						: 'Élève la forme en volume quand la carte est inclinée. À zéro, elle reste à plat.'}
				shapes={heightShapes}
				read={height}
				apply={(ids, meters, options) => editor[hover ? 'setShapeHoverHeight' : 'setShapeHeight'](ids, toModel(meters), options)}
				note={lines && shapes.some(shape => (effectiveHeight(shape) ?? 0) > 0) ? <WallThicknessNote shapes={shapes} /> : undefined}
			/>

			<MetersField
				label="Hauteur au-dessus du sol"
				help={hover
					? 'Décolle le volume du sol au survol. À zéro, comme à l\'état normal.'
					: 'Décolle le volume du sol quand la carte est inclinée. Sa hauteur reste son épaisseur.'}
				shapes={elevationShapes}
				read={elevation}
				apply={(ids, meters, options) => editor[hover ? 'setShapeHoverElevation' : 'setShapeElevation'](ids, toModel(meters), options)}
			/>
		</>
	);
});

/** Le mur prend l'épaisseur du trait quand elle est en mètres : une épaisseur en pixels ne dit rien de sa taille au sol. */
const WallThicknessNote = ({ shapes }: { shapes: Shape[]; }) => {
	// Un thème remplace le style de la forme, et ne règle son trait qu'en pixels.
	const styles = shapes.map(shape => (themeOf(shape) ? undefined : shape.style));
	const thicknesses = new Set(styles.map(lineWallThicknessMeters));
	const fromStroke = styles.every(style => style?.borderWidthUnit === 'm');

	if (thicknesses.size > 1) return <>Épaisseur du mur : celle du trait, ou 20 cm pour un trait en pixels.</>;

	const thickness = formatDistance([...thicknesses][0]!);

	return fromStroke
		? <>Épaisseur du mur : {thickness}, celle du trait.</>
		: <>Épaisseur du mur : {thickness}. Réglez l'épaisseur du trait en mètres pour la choisir.</>;
};

const MetersField = ({ label, help, shapes, read, apply, note }: {
	label: string;
	help: string;
	note?: React.ReactNode;
	shapes: Shape[];
	read: (shape: Shape) => number | undefined;
	apply: (shapeIds: string[], meters: number, options?: DrawTriggerOptions) => void;
}) => {
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const mixed = new Set(shapes.map(read)).size > 1;
	const value = read(shapes[0]!) ?? 0;

	const save = (input: number | undefined, options?: DrawTriggerOptions) => {
		const meters = Number(input);
		apply(shapes.map(shape => shape.id), meters > 0 ? meters : 0, options);
	};

	return (
		<Field label={<>{label}<HelpTip>{help}</HelpTip></>}>
			<View gap={4}>
				<SliderInputNumber
					value={value}
					min={0}
					max={SLIDER_MAX_HEIGHT_METERS}
					inputMax={MAX_HEIGHT_METERS}
					step={1}
					suffix="m"
					onChange={input => save(input, LIVE)}
					onAfterChange={input => save(input)}
				/>

				{mixed && (
					<View size={12} color={tokens.text.muted}>
						Valeurs différentes
					</View>
				)}

				{!!note && <View size={12} color={tokens.text.muted}>{note}</View>}
			</View>
		</Field>
	);
};

export default ThreeDOptions;
